'use client';

/**
 * <BadgeIcon> — render a badge / level icon from the value stored in the
 * `icon` column of the `badges` / `levels` tables.
 *
 * Three input shapes are supported:
 *
 *   1. Absolute URL ("/uploads/badges/foo.png", "https://...") — render
 *      via <Image> (admin-uploaded artwork).
 *   2. Kebab-case lucide icon name ("graduation-cap", "shield-alert",
 *      "trophy") — resolve via the static map below. Static imports are
 *      MANDATORY because Next.js Turbopack/SWC tree-shakes
 *      `import * as` namespace access — dynamic property access against
 *      a `* as` namespace would return `undefined` at runtime for icons
 *      that aren't statically imported elsewhere in the bundle.
 *   3. Anything else / not in the map — fall back to `Fallback`
 *      (default Crown).
 *
 * The map currently covers every icon in the default seed
 * (src/database/seed/portal-global/data/badges.json + levels.json).
 * Custom badges with non-seed icon names can either:
 *   - upload an actual image URL (URL path renders via <Image>), or
 *   - have their icon name added to ICON_MAP below + redeploy.
 */

import * as React from 'react';
import Image from 'next/image';
import {
  Award,
  BookOpenCheck,
  Crown,
  Flame,
  Footprints,
  GraduationCap,
  Medal,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Star,
  Trophy,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Static map of every kebab-case icon name shipped in the default seed.
 * KEEP THIS IN SYNC with `src/database/seed/portal-global/data/badges.json`
 * AND `src/database/seed/portal-global/data/levels.json` in the BE repo.
 * If you add a new seed icon, add the import + map entry in the same PR.
 */
const ICON_MAP: Record<string, LucideIcon> = {
  // Badges (seed)
  footprints: Footprints,
  'shield-alert': ShieldAlert,
  flame: Flame,
  'graduation-cap': GraduationCap,
  'book-open-check': BookOpenCheck,
  'shield-check': ShieldCheck,
  trophy: Trophy,
  crown: Crown,
  medal: Medal,
  // Levels (seed)
  shield: Shield,
  star: Star,
  // Generic fallback (intentionally redundant — lets callers pass `award`
  // explicitly without falling through to the default fallback)
  award: Award,
};

export interface BadgeIconProps {
  /** Value of `badge.icon` / `level.icon` from the BE (URL OR lucide name). */
  name: string | null | undefined;
  /** Tailwind size + colour classes. Default: `h-12 w-12`. */
  className?: string;
  /** Component shown when `name` is empty or doesn't resolve. Default: Crown. */
  Fallback?: LucideIcon;
  /** Alt text when rendering as an Image. */
  alt?: string;
}

function isUrl(value: string): boolean {
  return (
    value.startsWith('/') ||
    value.startsWith('http://') ||
    value.startsWith('https://') ||
    value.startsWith('data:')
  );
}

export function BadgeIcon({
  name,
  className,
  Fallback = Crown,
  alt,
}: BadgeIconProps) {
  if (!name) {
    return <Fallback className={cn('h-12 w-12', className)} aria-hidden />;
  }

  if (isUrl(name)) {
    return (
      <div className="relative h-full w-full">
        <Image
          src={name}
          alt={alt ?? ''}
          fill
          className={cn('object-contain', className)}
          unoptimized
        />
      </div>
    );
  }

  const Icon = ICON_MAP[name.toLowerCase()] ?? Fallback;
  return <Icon className={cn('h-12 w-12', className)} aria-hidden />;
}
